import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PassAlgarveLogo, PassAlgarveQrMark } from "@/components/PassAlgarveLogo";
import SiteFooter from "@/components/SiteFooter";
import PartnerLogos from "@/components/PartnerLogos";
import {
  Ship, Waves, Fish, Wine, ChevronRight, ChevronDown, Check, Menu, X,
  Star, Smartphone, QrCode, MapPin, Clock, Crown, Sparkles, Heart, Mountain,
  Gem, Compass, Users, Camera, ArrowRight, Calendar, Award, Globe, Mail,
  Facebook, Youtube, MessageCircle, Lock, CreditCard, Headphones
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { loc } from "@/lib/locale";
import Seo, { ORG_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/kjwrcltp_foto-banner.png";
const CARD_3D_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/uzxdncv1_card-pass-banner.png";

// Icon registry by tier
const TIER_ICON = { explorer: Compass, premium: Gem, family: Users, romantic: Heart, adventure: Mountain };

// Default fallback passport images
const DEFAULT_TIER_IMAGES = {
  explorer: "https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=800&q=80",
  premium: "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=800&q=80",
  family: "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=800&q=80",
  romantic: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80",
  adventure: "https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?w=800&q=80",
};

// Experiences (used in featured section — pulled from backend attractions fallback)
const FALLBACK_EXPERIENCES = [
  {
    id: "benagil-cave",
    name: "Grutas de Benagil",
    location: "Lagos",
    duration: "2h30",
    rating: 4.9,
    reviews: 320,
    price: 35,
    category: "boat",
    image: "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?w=800&q=80",
    description: "Explore as famosas grutas e formações rochosas.",
  },
  {
    id: "wine-dine",
    name: "Wine & Dine Experience",
    location: "Lagos",
    duration: "3h",
    rating: 4.8,
    reviews: 210,
    price: 65,
    category: "gastro",
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=800&q=80",
    description: "Atração gastronómica com vinhos locais e sabores únicos.",
  },
  {
    id: "sunset",
    name: "Sunset em Alto-Mar",
    location: "Albufeira",
    duration: "2h",
    rating: 4.9,
    reviews: 180,
    price: 40,
    category: "sunset",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
    description: "Viva o pôr-do-sol mais mágico do Algarve.",
  },
  {
    id: "buggy",
    name: "Buggy Tour Off-Road",
    location: "Portimão",
    duration: "4h",
    rating: 4.7,
    reviews: 98,
    price: 90,
    category: "adventure",
    image: "https://images.unsplash.com/photo-1532751203793-812308a10d8e?w=800&q=80",
    description: "Aventura off-road por paisagens incríveis do Algarve.",
  },
];

const CATEGORIES = [
  { id: "all", label: "Todas", Icon: Sparkles },
  { id: "boat", label: "Passeios de Barco", Icon: Ship },
  { id: "gastro", label: "Gastronomia", Icon: Wine },
  { id: "sunset", label: "Sunset", Icon: Sparkles },
  { id: "adventure", label: "Aventura", Icon: Mountain },
  { id: "family", label: "Família", Icon: Users },
  { id: "relax", label: "Relaxamento", Icon: Heart },
];

const ALGARVE_CITIES = [
  { name: "Sagres", x: 5, y: 78 },
  { name: "Lagos", x: 22, y: 70 },
  { name: "Portimão", x: 35, y: 65 },
  { name: "Albufeira", x: 55, y: 70 },
  { name: "Faro", x: 73, y: 75 },
  { name: "Tavira", x: 88, y: 65 },
];

const TESTIMONIALS = [
  { name: "Sarah & James", countryKey: "landing.t1c", flag: "🇬🇧", textKey: "landing.t1", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=80" },
  { name: "Marie Dubois", countryKey: "landing.t2c", flag: "🇫🇷", textKey: "landing.t2", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&q=80" },
  { name: "Carlos M.", countryKey: "landing.t3c", flag: "🇪🇸", textKey: "landing.t3", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80" },
];

const FAQS = [1, 2, 3, 4, 5].map((n) => ({ qk: `landing.faqQ${n}`, ak: `landing.faqA${n}` }));

// Smart icon picker for an attraction (used by passport cards on the landing).
// Looks at the explicit `category` first, then falls back to keyword matching
// against the attraction name.
function pickAttractionIcon(attr) {
  const cat = (attr.category || "").toLowerCase();
  const name = (attr.name || "").toLowerCase();
  if (cat === "boat" || /barco|gruta|benagil|piedade|golfin/i.test(name)) return Ship;
  if (cat === "gastro" || /happy|wine|tapas|bon vivant|mimar|bom dia|caf[ée]|gastr/i.test(name)) return Wine;
  if (cat === "adventure" || /aventura|trilha|trail|kayak|surf/i.test(name)) return Mountain;
  if (cat === "family" || /zoo|fam[ií]lia|crian/i.test(name)) return Users;
  if (cat === "relax" || /spa|relax/i.test(name)) return Heart;
  if (cat === "sunset" || /sunset|p[oô]r[\s-]?do[\s-]?sol/i.test(name)) return Sparkles;
  return Compass;
}

// ============================================================
// Countdown — 48h rolling
// ============================================================
const useCountdown = (rollingHours = 48) => {
  const KEY = "pass_offer_deadline";
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let deadline = parseInt(localStorage.getItem(KEY) || "0", 10);
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + rollingHours * 3600 * 1000;
      localStorage.setItem(KEY, deadline.toString());
    }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [rollingHours]);

  const deadline = parseInt(localStorage.getItem(KEY) || "0", 10) || (Date.now() + rollingHours * 3600 * 1000);
  const diff = Math.max(0, deadline - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
};

// ============================================================
// Main
// ============================================================
export default function LandingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, language, localizePath } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [passportTypes, setPassportTypes] = useState([]);
  const [ptLoading, setPtLoading] = useState(true);
  const [ptError, setPtError] = useState(false);
  const [attractions, setAttractions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const countdown = useCountdown(48);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setPtLoading(true);
      setPtError(false);
      try {
        const [pts, attrs] = await Promise.all([
          axios.get(`${API}/passport-types`),
          axios.get(`${API}/attractions`),
        ]);
        const sorted = [...(pts.data || [])].sort((a, b) => (a.display_order || a.price || 0) - (b.display_order || b.price || 0));
        setPassportTypes(sorted);
        setAttractions(attrs.data || []);
      } catch (e) {
        console.error("Failed to load passport-types/attractions:", e);
        setPtError(true);
      } finally {
        setPtLoading(false);
      }
    };
    fetchData();
    // Auto-retry once after 4s if it failed (handles transient backend hiccups)
    const retry = setTimeout(() => {
      if (passportTypes.length === 0) fetchData();
    }, 4000);
    return () => clearTimeout(retry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (passportTypeId = null) => {
    if (!user) { navigate("/register"); return; }
    const targetId = passportTypeId || (passportTypes.length > 0 ? passportTypes[0].id : null);
    setIsLoading(true);
    try {
      const r = await axios.post(`${API}/checkout/create-session`,
        { origin_url: window.location.origin, passport_type_id: targetId },
        { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(t("landing.checkoutError"));
      setIsLoading(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      toast.error(t("landing.newsletterInvalid"));
      return;
    }
    toast.success(t("landing.newsletterSuccess"));
    setNewsletterEmail("");
  };

  const NAV_ITEMS = [
    { label: t("nav.home"), id: "hero" },
    { label: t("nav.experiences"), id: "experiences" },
    { label: t("nav.how"), id: "how", external: "/como-funciona" },
    { label: t("nav.passports"), id: "passport" },
    { label: t("nav.partners"), id: "partners" },
    { label: t("nav.about"), id: "about", external: "/sobre" },
  ];

  // Build categories dynamically from real attractions, plus the "all" option.
  // Falls back to a sensible default when no categories are present.
  const CATEGORY_META = {
    boat:      { label: t("categories.boat"), Icon: Ship },
    gastro:    { label: t("categories.gastro"),       Icon: Wine },
    sunset:    { label: t("categories.sunset"),            Icon: Sparkles },
    adventure: { label: t("categories.adventure"),          Icon: Mountain },
    family:    { label: t("categories.family"),           Icon: Users },
    relax:     { label: t("categories.relax"),       Icon: Heart },
  };
  const dynamicCategories = (() => {
    const present = Array.from(new Set(
      attractions
        .map(a => a.category)
        .filter(c => c && CATEGORY_META[c])
    ));
    const cats = [{ id: "all", label: t("categories.all"), Icon: Sparkles }];
    present.forEach(c => cats.push({ id: c, ...CATEGORY_META[c] }));
    return cats;
  })();

  const visibleAttractions = activeCategory === "all"
    ? attractions
    : attractions.filter(a => a.category === activeCategory);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
      <Seo
        title={language === "en"
          ? "PASS Algarve | Attractions, Boat Tours and Experiences"
          : "PASS Algarve | Atrações, Passeios de Barco e Experiências"}
        description={language === "en"
          ? "What to do in the Algarve? With PASS Algarve enjoy the best attractions and experiences in Lagos: boat tours, dolphin watching, the Ponta da Piedade caves and much more — all in a single pass with one QR Code."
          : "O que fazer no Algarve? Com o PASS Algarve vive as melhores atrações e experiências em Lagos: passeios de barco, observação de golfinhos, grutas da Ponta da Piedade e muito mais — tudo num só passe com QR Code."}
        canonicalPath="/"
        jsonLd={[ORG_JSONLD, WEBSITE_JSONLD]}
      />
      {/* ============== HEADER ============== */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-[#071A35] shadow-lg" : "bg-transparent"
        }`}
        data-testid="header-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => scrollTo("hero")} className="flex items-center" data-testid="header-logo">
              <PassAlgarveLogo size="small" />
            </button>

            <nav className="hidden xl:flex items-center gap-6">
              {NAV_ITEMS.map((item) => (
                item.external ? (
                  <Link
                    key={item.id}
                    to={localizePath(item.external)}
                    className="text-white/85 hover:text-[#D4A85D] font-medium text-xs uppercase tracking-wider transition-colors"
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="text-white/85 hover:text-[#D4A85D] font-medium text-xs uppercase tracking-wider transition-colors"
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </nav>

            <div className="hidden xl:flex items-center gap-3">
              <LanguageSwitcher variant="navy" />
              <Link
                to="/login"
                className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-6 py-2.5 text-xs uppercase tracking-wider shadow-lg transition-colors"
                data-testid="header-login-btn"
              >
                {t("nav.login")}
              </Link>
            </div>

            <div className="xl:hidden flex items-center gap-2">
              <LanguageSwitcher variant="navy" />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="xl:hidden bg-[#071A35] border-t border-white/10 overflow-hidden"
            >
              <div className="px-4 py-6 space-y-3">
                {NAV_ITEMS.map((item) => (
                  item.external ? (
                    <Link
                      key={item.id}
                      to={localizePath(item.external)}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full text-left text-white/85 hover:text-[#D4A85D] text-sm font-medium py-2 uppercase tracking-wider"
                      data-testid={`mobile-nav-${item.id}`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className="block w-full text-left text-white/85 hover:text-[#D4A85D] text-sm font-medium py-2 uppercase tracking-wider"
                      data-testid={`mobile-nav-${item.id}`}
                    >
                      {item.label}
                    </button>
                  )
                ))}
                <div className="pt-4 flex flex-col gap-3">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md py-3 text-center text-sm uppercase tracking-wider transition-colors"
                    data-testid="mobile-login-btn"
                  >
                    {t("nav.login")}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ============== HERO ============== */}
      <section id="hero" className="relative min-h-[78vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Background image */}
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Algarve coast" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071A35]/95 via-[#071A35]/55 to-[#071A35]/45" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6 text-white"
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight">
                {t("landing.heroTitle1")}<br />
                <span className="text-[#D4A85D]">{t("landing.heroTitle2")}</span><br />
                {t("landing.heroTitle3")}
              </h1>
              <p className="mt-6 text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
                {t("landing.heroSubtitle")}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => scrollTo("passport")}
                  className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-8 py-6 text-sm uppercase tracking-wider shadow-xl group"
                  data-testid="hero-cta-primary"
                >
                  {t("landing.buyNow")}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => scrollTo("experiences")}
                  variant="outline"
                  className="bg-transparent border-2 border-white/70 text-white hover:bg-white/10 font-bold rounded-md px-8 py-6 text-sm uppercase tracking-wider"
                  data-testid="hero-cta-secondary"
                >
                  {t("landing.exploreAttractions")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>

              {/* Social proof */}
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-2 rounded-lg border border-white/15">
                  <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#4285F4" d="M45 24c0-1.6-.1-3.1-.4-4.5H24v9h11.8c-.5 2.7-2 5-4.3 6.6v5.5h6.9c4-3.7 6.6-9.2 6.6-16.6z"/><path fill="#34A853" d="M24 46c5.8 0 10.7-1.9 14.3-5.2l-6.9-5.5c-1.9 1.3-4.4 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.6C8 41 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.8 28.5C11.3 27 11 25.6 11 24s.3-3 .8-4.5V13.9H4.5C2.9 17 2 20.4 2 24s.9 7 2.5 10.1l7.3-5.6z"/><path fill="#EA4335" d="M24 10.5c3.2 0 6 1.1 8.3 3.2l6.2-6.2C34.7 4 29.8 2 24 2c-8.6 0-16 5-19.5 11.9l7.3 5.6C13.5 14.3 18.3 10.5 24 10.5z"/></svg>
                  <div className="text-white">
                    <div className="text-xs font-semibold">Google</div>
                    <div className="text-xs text-[#D4A85D]">★★★★★ 4.8/5</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-2 rounded-lg border border-white/15">
                  <Star className="w-5 h-5 text-emerald-400 fill-emerald-400" />
                  <div className="text-white">
                    <div className="text-xs font-semibold">Trustpilot</div>
                    <div className="text-xs text-[#D4A85D]">★★★★★ 4.7/5</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="flex -space-x-2 flex-shrink-0">
                    {[1,2,3,4].map(i => (
                      <img key={i} src={`https://i.pravatar.cc/40?img=${i+10}`} alt="" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                    ))}
                  </div>
                  <div className="text-white text-xs flex-1 min-w-0">
                    <div className="font-semibold">+5.630</div>
                    <div className="text-white/70 leading-snug">{t("landing.touristsUse")}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — 3D card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="lg:col-span-4 hidden lg:block"
            >
              <img
                src={CARD_3D_IMAGE}
                alt="PASS Algarve Card"
                className="w-full max-w-[520px] mx-auto drop-shadow-[0_25px_60px_rgba(212,168,93,0.35)] scale-110"
              />
            </motion.div>

            {/* Right side stats */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:col-span-2 flex lg:flex-col gap-3 lg:gap-4 justify-center"
            >
              {[
                { icon: Award, value: "150+", label: t("landing.statPartners") },
                { icon: Star, value: "200+", label: t("landing.statAttractions") },
                { icon: Users, value: "5.630+", label: t("landing.statTourists") },
              ].map((s, i) => (
                <div key={i} className="bg-white/8 backdrop-blur-md border border-white/15 rounded-xl p-3 lg:p-4 text-center min-w-[110px]">
                  <s.icon className="w-5 h-5 text-[#D4A85D] mx-auto mb-1" />
                  <div className="font-bold text-white text-xl lg:text-2xl leading-tight">{s.value}</div>
                  <div className="text-white/70 text-[10px] uppercase tracking-wider leading-tight mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============== COMO FUNCIONA ============== */}
      <section id="how" className="py-20 lg:py-28 bg-white" data-testid="how-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl lg:text-3xl font-bold text-[#071A35] uppercase tracking-[0.2em] mb-16">{t("sections.howItWorks")}</h2>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-6 relative">
            {[
              { num: "01", Icon: Crown, title: t("how.step1Title"), desc: t("how.step1Desc") },
              { num: "02", Icon: QrCode, title: t("how.step2Title"), desc: t("how.step2Desc") },
              { num: "03", Icon: MapPin, title: t("how.step3Title"), desc: t("how.step3Desc") },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center relative"
              >
                {i < 2 && (
                  <div className="hidden md:block absolute top-12 left-[58%] w-[84%] h-px bg-gradient-to-r from-[#D4A85D]/30 to-transparent" />
                )}
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4A85D]/15 to-[#D4A85D]/5 border border-[#D4A85D]/30 flex items-center justify-center">
                    <step.Icon className="w-10 h-10 text-[#D4A85D]" strokeWidth={1.4} />
                  </div>
                  <span className="absolute -top-2 -left-2 w-9 h-9 rounded-full bg-[#071A35] text-white text-xs font-bold flex items-center justify-center shadow-md">{step.num}</span>
                </div>
                <h3 className="font-bold text-[#071A35] uppercase tracking-wider text-sm mb-2">{step.title}</h3>
                <p className="text-[#071A35]/60 text-sm max-w-xs mx-auto">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TIPOS DE PASSAPORTE ============== */}
      <section id="passport" className="py-20 lg:py-28 bg-[#071A35] relative overflow-hidden" data-testid="passport-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#051428] via-[#071A35] to-[#051428]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h2 className="text-center text-2xl lg:text-3xl font-bold text-white uppercase tracking-[0.2em] mb-12">{t("sections.passportTypes")}</h2>

          {ptLoading ? (
            <div className="text-center text-white/50 py-12" data-testid="passport-types-loading">{t("passports.loading")}</div>
          ) : ptError ? (
            <div className="text-center py-12 max-w-md mx-auto" data-testid="passport-types-error">
              <p className="text-white/80 mb-4">{t("landing.loadError")}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 rounded-full bg-[#D4A85D] hover:bg-[#c19748] text-[#071A35] font-semibold transition-colors"
                data-testid="passport-types-retry-btn"
              >
                {t("landing.tryAgain")}
              </button>
            </div>
          ) : passportTypes.length === 0 ? (
            <div className="text-center text-white/50 py-12">{t("offer.noPassports")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {passportTypes.map((pt, idx) => {
                const Icon = TIER_ICON[pt.tier] || Crown;
                const img = pt.image_url || DEFAULT_TIER_IMAGES[pt.tier] || DEFAULT_TIER_IMAGES.explorer;
                return (
                  <motion.div
                    key={pt.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="group bg-[#0a2547]/60 border border-[#D4A85D]/15 rounded-2xl overflow-hidden hover:border-[#D4A85D]/60 hover:-translate-y-1 transition-all duration-300 shadow-xl"
                    data-testid={`passport-card-${pt.id}`}
                  >
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={img}
                        alt={pt.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => { e.target.src = DEFAULT_TIER_IMAGES[pt.tier] || DEFAULT_TIER_IMAGES.explorer; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a2547] via-[#0a2547]/30 to-transparent" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 overflow-hidden" data-testid={`card-qr-icon-${pt.id}`}>
                          <PassAlgarveQrMark size={36} />
                        </div>
                        <div>
                          <p className="text-[#D4A85D] text-[10px] uppercase tracking-widest font-medium">PASS</p>
                          <p className="text-white font-bold uppercase text-lg leading-tight">{(loc(pt, "name", language) || "").replace(/PASS\s*/i, "")}</p>
                        </div>
                      </div>
                      <p className="text-white/60 text-xs mb-3 min-h-[2rem]">{loc(pt, "tagline", language) || loc(pt, "description", language)}</p>

                      {/* Included attractions list (max 5) */}
                      <ul className="space-y-1.5 mb-5 min-h-[4.5rem]" data-testid={`included-attractions-${pt.id}`}>
                        {(pt.attractions || []).map((aid) => {
                          const a = attractions.find(x => x.id === aid);
                          if (!a) return null;
                          const AIcon = pickAttractionIcon(a);
                          return (
                            <li key={aid} className="flex items-start gap-2 text-white/85 text-xs">
                              <AIcon className="w-3.5 h-3.5 text-[#D4A85D] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                              <span className="leading-snug">{loc(a, "name", language)}</span>
                            </li>
                          );
                        })}
                        {(pt.attractions || []).length === 0 && (
                          <li className="text-white/40 text-xs italic">{t("offer.soonAttractions")}</li>
                        )}
                      </ul>

                      <div className="flex items-end justify-between gap-2">
                        <div>
                          <p className="text-white/40 text-[10px] uppercase tracking-wider">{t("landing.fromPrice")}</p>
                          <p className="text-white font-bold text-2xl">€{Math.round(pt.price)}</p>
                        </div>
                        <Button
                          onClick={() => navigate(language === "en"
                            ? `/en/passport/${pt.slug_en || pt.slug || pt.id}`
                            : `/passaporte/${pt.slug || pt.id}`)}
                          className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-4 py-2 text-xs uppercase tracking-wider"
                          data-testid={`view-${pt.id}`}
                        >
                          {t("attractions.buyPassport")}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== ATRAÇÕES EM DESTAQUE ============== */}
      <section id="experiences" className="py-20 lg:py-28 bg-white" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#071A35] uppercase tracking-[0.15em]">{t("sections.featuredAttractions")}</h2>
            <button onClick={() => scrollTo("passport")} className="text-[#D4A85D] hover:text-[#c9994a] text-xs uppercase tracking-widest font-bold flex items-center gap-1">
              {t("landing.viewAllAttractions")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {visibleAttractions.length === 0 ? (
            <div className="text-center text-[#071A35]/40 py-12 border border-dashed border-[#071A35]/15 rounded-2xl">
              {t("landing.noAttractionsCategory")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {visibleAttractions.map((a, i) => {
                const img = a.image_url || a.images?.[0] || DEFAULT_TIER_IMAGES.explorer;
                const rating = a.rating || 4.9;
                const reviews = a.reviews || a.review_count || 0;
                const duration = a.duration || "—";
                const location = a.location || "Lagos";
                const price = a.price_total || a.price || 0;
                return (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    data-testid={`attraction-card-${a.id}`}
                  >
                    <div className="relative h-40 overflow-hidden bg-gradient-to-br from-[#071A35] to-[#D4A85D]">
                      <img
                        src={img}
                        alt={a.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] font-bold text-[#071A35] flex items-center gap-1">
                          <Star className="w-3 h-3 fill-[#D4A85D] text-[#D4A85D]" /> {rating}{reviews > 0 ? ` (${reviews})` : ""}
                        </span>
                        {duration !== "—" && (
                          <span className="bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] font-bold text-[#071A35]">
                            {duration}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-[#071A35] text-base mb-1 line-clamp-1">{loc(a, "name", language)}</h3>
                      <p className="text-[#071A35]/60 text-xs flex items-center gap-1 mb-2">
                        <MapPin className="w-3 h-3 text-[#D4A85D]" /> {location}
                      </p>
                      <p className="text-[#071A35]/70 text-xs leading-relaxed mb-4 line-clamp-2 min-h-[32px]">{loc(a, "description", language) || (language === "en" ? "A must-see attraction in the Algarve." : "Atração imperdível no Algarve.")}</p>
                      <div className="flex items-end justify-between">
                        <div>
                          {price > 0 ? (
                            <>
                              <p className="text-[#071A35]/40 text-[10px] uppercase tracking-wider">{t("landing.fromPrice")}</p>
                              <p className="text-[#071A35] font-bold text-xl">€{Math.round(price)}</p>
                            </>
                          ) : (
                            <span className="bg-[#D4A85D]/15 text-[#D4A85D] text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">{t("attractions.includedFree")}</span>
                          )}
                        </div>
                        <Button
                          onClick={() => navigate(language === "en"
                            ? `/en/experiences/${a.slug_en || a.slug || a.id}`
                            : `/experiencias/${a.slug || a.id}`)}
                          className="bg-[#071A35] hover:bg-[#0a2547] text-white rounded-md px-4 py-2 text-[11px] uppercase tracking-wider font-bold"
                          data-testid={`view-more-${a.id}`}
                        >
                          {t("attractions.viewMore")}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Category filters — only show if we have categories beyond "all" */}
          {dynamicCategories.length > 1 && (
            <div className="mt-10 flex flex-wrap gap-2 justify-center">
              {dynamicCategories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider font-bold transition-all ${
                    activeCategory === c.id
                      ? "bg-[#071A35] text-white"
                      : "bg-transparent text-[#071A35]/60 hover:text-[#071A35] border border-[#071A35]/15"
                  }`}
                  data-testid={`category-${c.id}`}
                >
                  <c.Icon className="w-3.5 h-3.5" /> {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============== MAPA + DEPOIMENTOS ============== */}
      <section id="about" className="py-20 lg:py-28 bg-[#071A35] relative overflow-hidden" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Mapa */}
            <div className="lg:col-span-2">
              <h3 className="text-white font-bold uppercase tracking-[0.15em] text-lg mb-6">{t("sections.algarveMap")}</h3>
              <div className="relative aspect-[16/10] rounded-2xl bg-gradient-to-br from-[#0a2547] to-[#051428] border border-[#D4A85D]/15 overflow-hidden">
                {/* Stylized Algarve coast SVG path */}
                <svg viewBox="0 0 100 60" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                  <path
                    d="M2,55 Q8,52 15,55 Q22,58 30,55 Q40,58 50,60 Q60,58 70,60 Q80,58 90,55 Q95,55 98,55 L98,40 Q90,35 80,38 Q70,35 60,38 Q50,35 40,38 Q30,35 20,40 Q10,38 2,42 Z"
                    fill="url(#grad-coast)"
                    stroke="#D4A85D"
                    strokeWidth="0.3"
                    opacity="0.6"
                  />
                  <defs>
                    <linearGradient id="grad-coast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4A85D" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#D4A85D" stopOpacity="0.4" />
                    </linearGradient>
                  </defs>
                  {ALGARVE_CITIES.map((city) => (
                    <g key={city.name}>
                      <circle cx={city.x} cy={city.y - 15} r="0.8" fill="#D4A85D" />
                      <circle cx={city.x} cy={city.y - 15} r="2.5" fill="#D4A85D" opacity="0.25" />
                    </g>
                  ))}
                </svg>
                <div className="absolute inset-0 px-3 pt-3">
                  {ALGARVE_CITIES.map((city) => (
                    <span
                      key={city.name}
                      className="absolute text-white text-[10px] font-semibold whitespace-nowrap"
                      style={{ left: `${city.x}%`, top: `${city.y - 10}%`, transform: "translateX(-50%)" }}
                    >
                      {city.name}
                    </span>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => scrollTo("experiences")}
                variant="outline"
                className="mt-4 bg-transparent border-[#D4A85D]/40 text-[#D4A85D] hover:bg-[#D4A85D]/10 font-bold rounded-md text-xs uppercase tracking-wider"
                data-testid="explore-map-btn"
              >
                {t("landing.exploreMap")} <ArrowRight className="ml-2 w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Depoimentos */}
            <div className="lg:col-span-3">
              <h3 className="text-white font-bold uppercase tracking-[0.15em] text-lg mb-6">{t("landing.testimonialsTitle")}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {TESTIMONIALS.map((tm, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-[#0a2547]/60 border border-[#D4A85D]/15 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-1 mb-2 text-[#D4A85D]">
                      <Star className="w-3 h-3 fill-current" />
                      <span className="text-xs font-bold">5.0</span>
                    </div>
                    <p className="text-white/80 text-xs leading-relaxed mb-4 italic">"{t(tm.textKey)}"</p>
                    <div className="flex items-center gap-3">
                      <img src={tm.avatar} alt={tm.name} className="w-9 h-9 rounded-full object-cover border border-[#D4A85D]/30" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-xs">{tm.name}</p>
                        <p className="text-white/50 text-[10px]">{t(tm.countryKey)}</p>
                      </div>
                      <span className="text-lg">{tm.flag}</span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Instagram strip */}
              <div className="mt-6 flex items-center gap-3 flex-wrap bg-[#0a2547]/40 rounded-xl p-4 border border-[#D4A85D]/10">
                <div className="flex -space-x-2">
                  {[5,6,7,8,9].map(i => (
                    <img key={i} src={`https://i.pravatar.cc/40?img=${i+20}`} className="w-7 h-7 rounded-full border-2 border-[#071A35]" alt="" />
                  ))}
                </div>
                <span className="text-white/70 text-xs flex-1">{t("offer.joinTravelers")} <strong className="text-white">{t("offer.travelersSatisfied")}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PARCEIROS ============== */}
      <section id="partners" className="py-16 bg-white" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-[#071A35] font-bold uppercase tracking-[0.15em] text-base mb-8">{t("sections.officialPartners")}</h3>
          <PartnerLogos />
        </div>
      </section>

      {/* ============== FAQ + OFERTA LIMITADA ============== */}
      <section id="faq" className="py-20 lg:py-28 bg-white" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* FAQs */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#071A35] uppercase tracking-[0.15em] mb-8">{t("sections.faq")}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FAQS.map((f, i) => (
                  <div key={i} className="border border-[#071A35]/10 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-[#071A35]/5"
                    >
                      <span className="text-[#071A35] text-sm font-medium">{t(f.qk)}</span>
                      <span className="text-[#D4A85D] text-xl font-light flex-shrink-0">{openFaq === i ? "−" : "+"}</span>
                    </button>
                    {openFaq === i && (
                      <div className="px-4 pb-4 text-[#071A35]/70 text-xs leading-relaxed border-t border-[#071A35]/5 pt-3">
                        {t(f.ak)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Oferta Limitada Card */}
            <div className="bg-[#071A35] rounded-2xl p-6 lg:p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#D4A85D]/10 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="text-white font-bold text-xl mb-2">{t("offer.limitedTitle")}</h3>
                <p className="text-white/70 text-sm mb-6">{t("offer.limitedDesc")}</p>

                <div className="grid grid-cols-4 gap-2 mb-6">
                  {[
                    { val: countdown.days, lbl: t("offer.days") },
                    { val: countdown.hours, lbl: t("offer.hours") },
                    { val: countdown.minutes, lbl: t("offer.minutes") },
                    { val: countdown.seconds, lbl: t("offer.seconds") },
                  ].map((c, i) => (
                    <div key={i} className="bg-white/8 rounded-lg p-3 text-center border border-[#D4A85D]/20">
                      <div className="text-[#D4A85D] font-bold text-2xl tabular-nums">{String(c.val).padStart(2, "0")}</div>
                      <div className="text-white/50 text-[10px] uppercase tracking-wider mt-0.5">{c.lbl}</div>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => scrollTo("passport")}
                  className="w-full bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md py-6 text-sm uppercase tracking-wider shadow-xl"
                  data-testid="offer-buy-btn"
                >
                  {t("offer.buyNow")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA BANNER ============== */}
      <section className="py-12 bg-white" data-testid="home-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#071A35] rounded-3xl p-8 lg:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h2 className="text-2xl lg:text-3xl font-bold mb-3">{t("pd.ctaTitle")}</h2>
              <p className="text-white/75 text-sm leading-relaxed mb-6">
                {t("pd.ctaDescA")}<span className="text-[#D4A85D] font-semibold">PASS Algarve</span>{t("pd.ctaDescB")}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => scrollTo("passport")}
                  className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-8 py-6 text-sm uppercase tracking-wider"
                  data-testid="home-cta-buy-btn"
                >
                  {t("landing.buyNow")}
                </Button>
                <Button
                  onClick={() => scrollTo("passport")}
                  variant="outline"
                  className="border-2 border-white/40 bg-transparent text-white hover:bg-white/10 font-bold rounded-md px-8 py-6 text-sm uppercase tracking-wider"
                  data-testid="home-cta-view-btn"
                >
                  {t("aboutPage.ctaView")}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-white">
              {[
                { Icon: Lock, title: t("aboutPage.mini1t"), desc: t("aboutPage.mini1s") },
                { Icon: CreditCard, title: t("aboutPage.mini2t"), desc: t("aboutPage.mini2s") },
                { Icon: Headphones, title: t("aboutPage.mini3t"), desc: t("aboutPage.mini3s") },
              ].map((f, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#D4A85D]/10 border border-[#D4A85D]/30 flex items-center justify-center mb-3">
                    <f.Icon className="w-5 h-5 text-[#D4A85D]" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-1">{f.title}</p>
                  <p className="text-white/55 text-[10px] leading-snug">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ============== FOOTER ============== */}
      <SiteFooter />
    </div>
  );
}